import React, { useEffect, useMemo, useState } from 'react';

import type { Leaves } from '@aline/common';
import { Select } from '@aline/common';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, ListItemText, Typography } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { AlineAutocompleteInput, AlineAvatar } from 'src/components';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import { WORKFLOW_LAUNCHER_LABEL } from 'src/components/workflow/workflow-helpers';
import { Apollo } from 'src/gql/generated';
import { getRecipientAssigneeDisplayDetails, getRecipientAvatarInfo } from 'src/helpers';
import { useGetCurrentDoc, useGetWorkflowWithSteps } from 'src/hooks';
import { roleAvatarColor } from 'src/theme';
import type { UserDefinedRoleSubset, UserSubset } from 'src/types';
import { getSigningStepsFromWorkflow } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

export const unassignedRecipientString = 'Unassigned';

interface NewFieldRecipient {
  __typename: 'NewFieldRecipient';
  email: string;
}
export interface ExistingFieldRecipient extends Pick<
  Apollo.WorkflowRecipient,
  'id' | '__typename' | 'type'
> {
  assignee: UserSubset | null;
  assigneeRole: UserDefinedRoleSubset | null;
  // A template's launcher recipient has no assignee or role yet; only the permission says who it is.
  permission: Pick<Apollo.WorkflowPermission, 'isLauncher'>;
}
type FieldRecipientOption = ExistingSignerRecipient | NewFieldRecipient;

type CustomAutocompleteProps = AutocompleteProps<FieldRecipientOption, false, false, false>;
type FilterOptions = CustomAutocompleteProps['filterOptions'];

const useStyles = makeStyles()(theme => ({
  input: {
    margin: theme.spacing(0.75),
  },
}));

interface FieldRecipientAutoCompleteProps {
  onChange: (value: FieldRecipientOption | null) => void;
  value: ExistingFieldRecipient | null;
  autoSelectFirstRecipient?: boolean;
  blurOnSelect?: CustomAutocompleteProps['blurOnSelect'];
  className?: string;
  dataCy?: string;
  disableCloseOnSelect?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}

export function FieldRecipientAutoComplete(
  props: FieldRecipientAutoCompleteProps
): JSX.Element | null {
  const {
    autoSelectFirstRecipient = true,
    blurOnSelect = true,
    className,
    dataCy = Select.components.fieldRecipientsAutocomplete.autocomplete,
    disableCloseOnSelect,
    disabled,
    fullWidth = true,
    onChange,
    value,
  } = props;

  const { classes } = useStyles();
  const { document } = useGetCurrentDoc();
  const [inputValue, setInputValue] = useState<string>('');
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
  const { loading, workflow } = useGetWorkflowWithSteps(document?.workflow?.id ?? null);
  const [highlightedOption, setHighlightedOption] = useState<FieldRecipientOption | null>(null);
  const options = useMemo(() => {
    return (
      getSigningStepsFromWorkflow(workflow)
        .flatMap((s): ExistingSignerRecipient[] => {
          const actions: ExistingSignerRecipient[] = [];
          // TS filter is funky with ExistingFieldRecipient so we have to do this to get types to play nice.
          for (const action of s.actions) {
            if (getIsSignerFieldRecipient(action)) actions.push(action);
          }

          return actions;
        })
        .sort((a, b) =>
          getRecipientAssigneeDisplayDetails(a).primary.localeCompare(
            getRecipientAssigneeDisplayDetails(b).primary
          )
        ) ?? []
    );
  }, [workflow]);
  const safeValue = options.find(o => o.id === value?.id) ?? null;

  const filterOptionsAndSuggestIfNeeded: FilterOptions = (options, filterOptionsState) => {
    // Order of keys matters and sets the search ranking.
    const optionKeys: Leaves<ExistingFieldRecipient>[] = [
      'assignee.displayName',
      'assignee.email',
      'assigneeRole.roleName',
    ];
    const sortedOptions = matchSorter<FieldRecipientOption>(options, inputValue, {
      keys: optionKeys,
    });

    return [
      ...sortedOptions,
      {
        __typename: 'NewFieldRecipient',
        email: filterOptionsState.inputValue,
      } satisfies NewFieldRecipient,
    ];
  };

  useEffect(() => {
    if (!autoSelectFirstRecipient || loading) return;

    const valueId = value?.id;
    const firstOption = options.at(0);
    const existsInOptions = Boolean(valueId) && options.some(o => o.id === valueId);

    if (firstOption && !existsInOptions) {
      onChange(firstOption);
      return;
    }

    if (valueId && !existsInOptions) onChange(null);
  }, [autoSelectFirstRecipient, loading, onChange, options, value?.id]);

  return (
    <Autocomplete<FieldRecipientOption, false, false, false>
      openOnFocus
      blurOnSelect={blurOnSelect}
      className={className}
      data-cy={dataCy}
      disableCloseOnSelect={disableCloseOnSelect}
      disabled={disabled}
      filterOptions={filterOptionsAndSuggestIfNeeded}
      fullWidth={fullWidth}
      getOptionKey={option =>
        option.__typename === 'NewFieldRecipient' ? option.email : option.id
      }
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value): boolean => {
        return 'id' in option && 'id' in value && option.id === value.id;
      }}
      loading={loading}
      open={autocompleteOpen}
      options={options}
      renderInput={params => {
        const { avatarColor } = getRecipientDisplayDetails(safeValue);

        return (
          <AlineAutocompleteInput
            placeholder="Search field recipients..."
            startAdornment={
              <AlineAvatar
                avatarInfo={getRecipientAvatarInfo(safeValue)}
                className={classes.input}
                styles={{ backgroundColor: avatarColor, size: 'md' }}
              />
            }
            {...params}
          />
        );
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <FieldRecipientAutoCompleteOption
              highlightedOptionId={
                highlightedOption && 'id' in highlightedOption ? highlightedOption.id : undefined
              }
              option={option}
            />
          </li>
        );
      }}
      slotProps={{
        listbox: {
          'data-cy': Select.components.fieldRecipientsAutocomplete.listBox,
        },
      }}
      value={options.find(o => o.id === value?.id) ?? null}
      onChange={(_, option) => onChange(option)}
      onClose={() => setAutocompleteOpen(false)}
      onFocus={() => setInputValue('')}
      onHighlightChange={(_, option) => setHighlightedOption(option)}
      onInputChange={(_, newValue, reason) => {
        // Don't override the focus-clear with the option label
        if (reason === 'reset' && autocompleteOpen) return;
        setInputValue(newValue);
      }}
      onOpen={() => {
        setInputValue('');
        setAutocompleteOpen(true);
      }}
    />
  );
}

export const useRecipientOptionStyles = makeStyles()(theme => ({
  icon: {
    height: 18,
    width: 18,
  },
  iconCircle: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    // Use min/max b/c when typing long strings in the autocomplete it preserves height/width and using normal CSS height/width does not.
    maxHeight: 24,
    maxWidth: 24,
    minHeight: 24,
    minWidth: 24,
  },
  newRecipient: {
    ...theme.appTypography.buttonSmall,
    color: theme.appTypography.buttonSmall.color,
    whiteSpace: 'nowrap',
  },
  optionContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  secondaryText: {
    color: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
  },
}));

interface NewRecipientOptionProps {
  text?: string;
}

export function NewRecipientOption({
  text = 'Add Recipient to Steps',
}: NewRecipientOptionProps): JSX.Element {
  const { classes } = useRecipientOptionStyles();

  return (
    <div
      className={classes.optionContainer}
      data-cy={Select.components.fieldRecipientsAutocomplete.addRecipientButton}
    >
      <div className={classes.iconCircle}>
        <CrossIcon className={classes.icon} />
      </div>
      <Typography className={classes.newRecipient}>{text}</Typography>
    </div>
  );
}

interface RecipientOptionProps {
  option: FieldRecipientOption;
  highlightedOptionId?: UUID;
  text?: string;
}

export function FieldRecipientAutoCompleteOption({
  option,
  text,
}: RecipientOptionProps): JSX.Element {
  const { classes } = useRecipientOptionStyles();
  const isNewRecipientOption = option.__typename === 'NewFieldRecipient';

  if (isNewRecipientOption) return <NewRecipientOption text={text} />;

  const { avatarColor, primaryText } = getRecipientDisplayDetails(option);
  const actionText = option.__typename === 'WorkflowRecipient' ? 'Sign' : 'Complete fields';

  return (
    <div
      className={classes.optionContainer}
      data-cy={Select.components.fieldRecipientsAutocomplete.recipientSignerOption}
    >
      <AlineAvatar
        avatarInfo={getRecipientAvatarInfo(option)}
        styles={{ backgroundColor: avatarColor, size: 'md' }}
      />
      <ListItemText
        classes={{ secondary: classes.secondaryText }}
        data-cy="recipient-option-text"
        primary={primaryText}
        secondary={actionText}
        slotProps={{
          primary: { align: 'left', noWrap: true, variant: 'body1' },
          secondary: { align: 'left', noWrap: true },
        }}
      />
    </div>
  );
}

export function getRecipientDisplayDetails(recipient: ExistingFieldRecipient | null): {
  primaryText: string;
  avatarColor?: string;
} {
  if (recipient?.permission.isLauncher)
    return { avatarColor: roleAvatarColor, primaryText: WORKFLOW_LAUNCHER_LABEL };

  const primaryTextParts: string[] = [];
  if (recipient?.assignee?.displayName) primaryTextParts.push(recipient.assignee.displayName);
  if (recipient?.assigneeRole?.roleName) {
    const hasAssignee = Boolean(primaryTextParts.length);
    primaryTextParts.push(
      hasAssignee ? `(${recipient.assigneeRole.roleName})` : recipient.assigneeRole.roleName
    );
  }
  if (!primaryTextParts.length) primaryTextParts.push(unassignedRecipientString);

  const avatarColor = recipient?.assignee
    ? recipient.assignee.avatarColor
    : recipient?.assigneeRole
      ? roleAvatarColor
      : undefined;

  return { avatarColor, primaryText: primaryTextParts.join(' ') };
}

type ExistingSignerRecipient = Omit<ExistingFieldRecipient, 'type'> & {
  type: Apollo.WorkflowRecipientType.Signer;
};

export function getIsSignerFieldRecipient(
  action: ExistingFieldRecipient
): action is ExistingSignerRecipient {
  return action.type === Apollo.WorkflowRecipientType.Signer;
}

function getOptionLabel(option: FieldRecipientOption): string {
  switch (option.__typename) {
    case 'NewFieldRecipient':
      return option.email;
    case 'WorkflowRecipient':
      if (option.permission.isLauncher) return WORKFLOW_LAUNCHER_LABEL;
      return option.assignee?.displayName ?? option.assigneeRole?.roleName ?? '';
  }
}
